import { z } from 'zod'

import { applyExhibitorEvent, type ExhibitorEvent } from '@ems/machines/exhibitor-application'
import type { StatusTone } from '@ems/ui/patterns/status-badge'

import { api } from '#admin/lib/api-client.ts'

/**
 * Floor sales, as the organizer sees them.
 *
 * The schema lives with the feature that calls the endpoint — the router guide's rule, and
 * also what makes the response *parsed* rather than asserted. The console is a static file on
 * CloudFront, so being a few minutes behind the API after a release is the normal state, and a
 * parse turns that into one legible error instead of a crash three components deep.
 *
 * **Every figure here was computed by the server.** Nothing in this file multiplies an area by
 * a rate: the pricing preview is an endpoint precisely so that the console and the invoice
 * cannot disagree, and a total recomputed in the browser would be a fifth call site for
 * invariant 05.
 */

/**
 * The eight states, in the order the flow walks them.
 *
 * Mirrors `packages/machines`'s `EXHIBITOR_STATES` — and the console could import that machine
 * instead of restating the list. It does not, deliberately: this is a **parse of what the
 * server sent**, and validating it against the client's idea of the states is what catches a
 * deploy where the two have drifted. The machine is for deciding which buttons exist.
 */
export const APPLICATION_STATUSES = [
  'draft',
  'submitted',
  'pending_verification',
  'approved',
  'payment_pending',
  'payment_completed',
  'rejected',
  'canceled',
] as const
export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number]

export const BOOTH_STATUSES = [
  'available',
  'selected',
  'reserved',
  'confirmed',
  'canceled',
] as const
export type BoothStatus = (typeof BOOTH_STATUSES)[number]

export const boothSchema = z.object({
  id: z.string(),
  code: z.string(),
  hall: z.string().nullable(),
  boothTypeId: z.string(),
  areaSqm: z.string(),
  ratePerSqm: z.number(),
  status: z.enum(BOOTH_STATUSES),
  applicationId: z.string().nullable(),
  isPremium: z.boolean(),
  // Where the floor plan draws the stand; null when nobody has placed it. `.default(null)`: an
  // older API without it reads as "not placed", and the plan arranges the stand.
  position: z.record(z.string(), z.unknown()).nullable().default(null),
})

export const applicationSchema = z.object({
  id: z.string(),
  code: z.string().nullable(),
  status: z.enum(APPLICATION_STATUSES),
  companyName: z.string(),
  // The company whose members act for the application. Null for an application with no company.
  // `.default(null)`: the console is deployed ahead of the API.
  companyId: z.string().nullable().default(null),
  contactName: z.string(),
  contactEmail: z.string(),
  country: z.string().nullable(),
  currency: z.string(),
  // The three documents the applicant uploads. `.default(null)`: the console is deployed ahead of
  // the API, and an older answer without them reads as "not uploaded".
  companyProfileKey: z.string().nullable().default(null),
  businessLicenceKey: z.string().nullable().default(null),
  taxDocumentKey: z.string().nullable().default(null),
  invoiceId: z.string().nullable(),
  submittedAt: z.string().nullable(),
  approvedAt: z.string().nullable(),
  rejectedAt: z.string().nullable(),
  rejectionReason: z.string().nullable(),
  paidAt: z.string().nullable(),
  canceledAt: z.string().nullable(),
  booths: z.array(boothSchema).optional(),
})

export const applicationListSchema = z.object({ items: z.array(applicationSchema) })
export const boothListSchema = z.object({ items: z.array(boothSchema) })

export type Booth = z.infer<typeof boothSchema>
export type Application = z.infer<typeof applicationSchema>

export function fetchApplications(signal?: AbortSignal): Promise<{ items: Application[] }> {
  return api.get('/v1/exhibitor/admin/applications', {
    schema: applicationListSchema,
    ...(signal ? { signal } : {}),
  })
}

export function fetchBooths(signal?: AbortSignal): Promise<{ items: Booth[] }> {
  return api.get('/v1/exhibitor/booths', {
    schema: boothListSchema,
    ...(signal ? { signal } : {}),
  })
}

/**
 * The organizer's worklist, in the order they act on it.
 *
 * Not "newest first". An application sitting in `submitted` is somebody waiting for a human,
 * and sorting by date buries it under this morning's drafts. The states are ordered by whose
 * turn it is: ours, then theirs, then done.
 */
const ACTION_ORDER: Record<ApplicationStatus, number> = {
  submitted: 0,
  pending_verification: 1,
  approved: 2,
  payment_pending: 3,
  draft: 4,
  payment_completed: 5,
  rejected: 6,
  canceled: 7,
}

export function byActionNeeded(a: Application, b: Application): number {
  const order = ACTION_ORDER[a.status] - ACTION_ORDER[b.status]
  return order === 0 ? a.companyName.localeCompare(b.companyName) : order
}

/** Whose move it is. Rendered beside the status, because the status alone does not say. */
export function waitingOn(status: ApplicationStatus): 'you' | 'exhibitor' | 'nobody' {
  if (status === 'submitted' || status === 'pending_verification' || status === 'approved') {
    return 'you'
  }
  if (status === 'draft' || status === 'payment_pending') return 'exhibitor'
  return 'nobody'
}

export type FloorSummary = { status: BoothStatus; count: number; areaSqm: number }

/**
 * The floor, by status.
 *
 * Counting rather than drawing. A real floor map is a plan-view of `booth.position`, and it is
 * the right thing to build — but the number an organizer asks for every morning is "how much
 * of the hall is sold", and that is a `group by`, not a canvas.
 *
 * `areaSqm` is summed here because it is arithmetic on a **dimension**, not on money. The
 * rates deliberately stay untouched: a hall total in rupiah would be this console computing a
 * figure the server also computes, and the two would drift.
 */
export function summarizeFloor(booths: readonly Booth[]): FloorSummary[] {
  const totals = new Map<BoothStatus, FloorSummary>()

  for (const booth of booths) {
    const entry = totals.get(booth.status) ?? { status: booth.status, count: 0, areaSqm: 0 }
    entry.count += 1
    entry.areaSqm += Number(booth.areaSqm)
    totals.set(booth.status, entry)
  }

  return BOOTH_STATUSES.filter((status) => totals.has(status)).map(
    (status) => totals.get(status) ?? { status, count: 0, areaSqm: 0 },
  )
}

/**
 * An application's status, as a tone — **how far along it is**, read from the status alone.
 *
 * It used to be derived from `waitingOn`, so every status waiting on an operator was `warning` —
 * including `approved`, which then wore a warning triangle on the one outcome that is good news.
 * Whose move it is already has its own column ("Waiting on"), so the badge no longer has to say it
 * twice, and an explicit table means a ninth status is a type error here rather than a guess.
 *
 * `submitted` and `pending_verification` stay `warning`: nothing is decided yet, and both need an
 * operator. `approved` is `info`, not `success`: the decision is good news, but the application is
 * not finished — there is an invoice to issue and money to collect — and a green tick beside it
 * would look the same as `payment_completed` in a long list, which is the difference finance reads
 * this screen for. Only the end of the road is green, as for a registration. `payment_pending` is
 * `pending` because the invoice is out and the money is owed.
 */
const APPLICATION_TONE: Record<ApplicationStatus, StatusTone> = {
  draft: 'neutral',
  submitted: 'warning',
  pending_verification: 'warning',
  approved: 'info',
  payment_pending: 'pending',
  payment_completed: 'success',
  rejected: 'danger',
  canceled: 'danger',
}

export function applicationTone(status: ApplicationStatus): StatusTone {
  return APPLICATION_TONE[status]
}

/**
 * A stand's status, as a tone.
 *
 * `confirmed` is the only success, and that is invariant 02 wearing interface clothes: a booth that
 * is paid for but still shows as `reserved` is floor space the map will offer to somebody else.
 */
const BOOTH_TONE: Record<BoothStatus, StatusTone> = {
  confirmed: 'success',
  reserved: 'warning',
  selected: 'pending',
  canceled: 'danger',
  available: 'info',
}

export function boothTone(status: BoothStatus): StatusTone {
  return BOOTH_TONE[status]
}

// --- one application ------------------------------------------------------------------------

const base = '/v1/exhibitor/admin/applications'

export function fetchApplication(id: string, signal?: AbortSignal): Promise<Application> {
  return api.get(`${base}/${id}`, { schema: applicationSchema, ...(signal ? { signal } : {}) })
}

/**
 * What the stands cost, line by line. **The server computes it** (invariant 05): the console
 * shows these figures and does no arithmetic on them.
 */
export const pricingSchema = z.object({
  currency: z.string(),
  lines: z.array(
    z.object({
      boothId: z.string(),
      boothCode: z.string(),
      areaSqm: z.string(),
      ratePerSqm: z.number(),
      taxRate: z.string(),
      lineSubtotal: z.number(),
      lineTax: z.number(),
      lineTotal: z.number(),
    }),
  ),
  subtotal: z.number(),
  taxTotal: z.number(),
  total: z.number(),
})

export type Pricing = z.infer<typeof pricingSchema>

export function fetchApplicationPricing(id: string, signal?: AbortSignal): Promise<Pricing> {
  return api.get(`${base}/${id}/pricing`, { schema: pricingSchema, ...(signal ? { signal } : {}) })
}

/**
 * The server prices an application only after approval, and refuses before that with a 409.
 * The page asks only when the answer can be a price.
 */
export function hasPricing(status: ApplicationStatus): boolean {
  return status === 'approved' || status === 'payment_pending'
}

export function verifyApplication(id: string): Promise<Application> {
  return api.post(`${base}/${id}/verify`, { schema: applicationSchema, body: {} })
}

export function approveApplication(id: string, note?: string): Promise<Application> {
  return api.post(`${base}/${id}/approve`, {
    schema: applicationSchema,
    body: note === undefined ? {} : { note },
  })
}

export function rejectApplication(id: string, reason: string): Promise<Application> {
  return api.post(`${base}/${id}/reject`, { schema: applicationSchema, body: { reason } })
}

/** Raises the stand rental invoice in billing. `dueAt` is an ISO instant. */
export function createBoothInvoice(id: string, dueAt?: string): Promise<Application> {
  return api.post(`${base}/${id}/invoice`, {
    schema: applicationSchema,
    body: dueAt === undefined ? {} : { dueAt },
  })
}

/** Voids the invoice, unless billing voided it already, and issues a new one (#57). */
export function recreateBoothInvoice(id: string, reason: string): Promise<Application> {
  return api.post(`${base}/${id}/reinvoice`, { schema: applicationSchema, body: { reason } })
}

/** Records a payment made outside billing. The server refuses it when an invoice is linked. */
export function markApplicationPaid(id: string, note?: string): Promise<Application> {
  return api.post(`${base}/${id}/mark-paid`, {
    schema: applicationSchema,
    body: note === undefined ? {} : { note },
  })
}

/**
 * Whether an operator can give the application other stands: until an invoice prices them. The
 * server holds the same rule (`select-booths/service.ts`).
 */
export function canAssignBooths(application: Pick<Application, 'status' | 'invoiceId'>): boolean {
  return (
    application.invoiceId === null &&
    (application.status === 'draft' ||
      application.status === 'submitted' ||
      application.status === 'pending_verification' ||
      application.status === 'approved')
  )
}

/** Replace the application's stands. A 409 names the stands that are not free. */
export function assignBooths(id: string, boothIds: string[]): Promise<Application> {
  return api.put(`${base}/${id}/booths`, { schema: applicationSchema, body: { boothIds } })
}

export function cancelApplication(id: string, reason?: string): Promise<Application> {
  return api.post(`${base}/${id}/cancel`, {
    schema: applicationSchema,
    body: reason === undefined ? {} : { reason },
  })
}

// --- documents --------------------------------------------------------------------------------

export type DocumentKind = 'company-profile' | 'business-license' | 'tax-document'

/** The three uploads, in the order the applicant's form asks for them. */
export const APPLICATION_DOCUMENTS: readonly {
  kind: DocumentKind
  label: string
  key: (application: Application) => string | null
}[] = [
  { kind: 'company-profile', label: 'Company profile', key: (a) => a.companyProfileKey },
  { kind: 'business-license', label: 'Business license', key: (a) => a.businessLicenceKey },
  { kind: 'tax-document', label: 'Tax document', key: (a) => a.taxDocumentKey },
]

/** A link that expires in five minutes. Get it on click, not on load. */
export function fetchDocumentLink(id: string, kind: DocumentKind) {
  return api.get(`${base}/${id}/documents/${kind}`, {
    schema: z.object({ url: z.string(), expiresInSeconds: z.number() }),
  })
}

// --- what an operator can do ------------------------------------------------------------------

export type ApplicationActions = {
  verify: boolean
  approve: boolean
  reject: boolean
  createInvoice: boolean
  markPaid: boolean
  /** An unpaid invoice is replaced, also after billing voided it. Not a machine event. */
  recreateInvoice: boolean
  cancel: boolean
}

export const NO_APPLICATION_ACTIONS: ApplicationActions = {
  verify: false,
  approve: false,
  reject: false,
  createInvoice: false,
  markPaid: false,
  recreateInvoice: false,
  cancel: false,
}

/**
 * The verbs this application accepts now.
 *
 * **The machine in `@ems/machines` decides.** The server moves the status with the same machine,
 * so the page cannot offer a button that the server then refuses. The two invoice verbs also need
 * an application with no invoice yet. The server checks that fact on the row, outside the machine.
 */
export function applicationActions(
  application: Pick<Application, 'status' | 'invoiceId'>,
): ApplicationActions {
  const accepts = (type: ExhibitorEvent['type']) =>
    applyExhibitorEvent(application.status, { type }) !== application.status
  const uninvoiced = application.invoiceId === null
  return {
    verify: accepts('VERIFY'),
    approve: accepts('APPROVE'),
    reject: accepts('REJECT'),
    createInvoice: accepts('CREATE_INVOICE') && uninvoiced,
    markPaid: accepts('MARK_PAID_NO_INVOICE') && uninvoiced,
    recreateInvoice: application.status === 'payment_pending' && !uninvoiced,
    cancel: accepts('CANCEL'),
  }
}

/**
 * What the application waits for, when the operator has no step forward on it. Shown beside the
 * actions, so an operator who sees no Approve or Invoice knows why. Null when the operator has a
 * step to take, or when the page says it already (a rejected or canceled application).
 */
export function waitingFor(status: ApplicationStatus): string | null {
  switch (status) {
    case 'draft':
      return 'Waiting for the applicant to submit the application.'
    case 'payment_pending':
      return 'Waiting for the exhibitor to pay the invoice.'
    case 'payment_completed':
      return 'Payment is complete. The stands are confirmed.'
    default:
      return null
  }
}

// --- the floor plan -----------------------------------------------------------------------------

/**
 * A booth class, such as "Shell scheme" or "Bare space". A stand charges its type's rate unless
 * the stand has a negotiated rate of its own. `taxRate` is a percentage, as a decimal string.
 */
export const boothTypeSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  currency: z.string(),
  ratePerSqm: z.number(),
  taxRate: z.string(),
  conventionPasses: z.number().int(),
  isActive: z.boolean(),
})

export type BoothType = z.infer<typeof boothTypeSchema>

/** The active booth classes. An inactive class is not listed. */
export function fetchBoothTypes(signal?: AbortSignal): Promise<{ items: BoothType[] }> {
  return api.get('/v1/exhibitor/booth-types', {
    schema: z.object({ items: z.array(boothTypeSchema) }),
    ...(signal ? { signal } : {}),
  })
}

/** Money and areas go over the wire as decimal strings, never as floats. */
export type BoothInput = {
  code: string
  hall?: string
  boothTypeId: string
  areaSqm: string
  ratePerSqmOverride?: string
  isPremium: boolean
  /** Where the floor plan draws the stand (`floor-plan/types.ts`). */
  position?: StandPosition
}

/**
 * A stand's place on the floor plan: canvas units (10 per meter) and degrees, the shape v1's
 * mapper stored and the v1 import writes.
 */
export type StandPosition = {
  x: number
  y: number
  width: number
  height: number
  rotation: number
}

/** What an edit may change. The code and the booth type are fixed once the stand exists. */
export type BoothChanges = {
  hall?: string
  areaSqm?: string
  ratePerSqmOverride?: string
  isPremium?: boolean
  /** Only these two: the other statuses follow an application, never an operator. */
  status?: 'available' | 'canceled'
  /** The stand's place on the floor plan. */
  position?: StandPosition
}

export function createBooth(input: BoothInput): Promise<Booth> {
  return api.post('/v1/exhibitor/admin/booths', { schema: boothSchema, body: input })
}

export function updateBooth(id: string, changes: BoothChanges): Promise<Booth> {
  return api.patch(`/v1/exhibitor/admin/booths/${id}`, { schema: boothSchema, body: changes })
}

export type BoothTypeInput = {
  code: string
  name: string
  description?: string
  currency: string
  ratePerSqm: string
  taxRate: string
  conventionPasses: number
}

export function createBoothType(input: BoothTypeInput): Promise<BoothType> {
  return api.post('/v1/exhibitor/admin/booth-types', { schema: boothTypeSchema, body: input })
}

export function updateBoothType(
  id: string,
  changes: Omit<BoothTypeInput, 'code'>,
): Promise<BoothType> {
  return api.patch(`/v1/exhibitor/admin/booth-types/${id}`, {
    schema: boothTypeSchema,
    body: changes,
  })
}
